import React, {Component, ErrorInfo, ReactNode} from 'react';
import {Platform, ScrollView, StyleSheet, TouchableOpacity, View} from 'react-native';

import {
  NAVIGATION_STATE_KEY,
  NAVIGATION_TIMESTAMP_KEY,
  NAVIGATION_URL_KEY,
} from '../constants/sessionStorageKeys';
import theme from '../style/theme';
import dev from '../utils/dev';
import {logError, safeExtractError} from '../utils/errorLogging';

import MonospaceText from './ui-controls/MonospaceText';
import Text from './ui-controls/Text';

type ErrorBoundaryProps = {
  children: ReactNode;
  message?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error: error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Safe error logging (prevents Hermes crashes)
    logError(error, 'ErrorBoundary');

    // Store error info in state for display
    this.setState({
      errorInfo: errorInfo,
    });

    // Optional error reporting callback (e.g., for Sentry)
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Clear navigation state on all platforms
    this.clearNavigationState();
  }

  clearNavigationState = () => {
    if (Platform.OS === 'web') {
      try {
        sessionStorage.removeItem(NAVIGATION_STATE_KEY);
        sessionStorage.removeItem(NAVIGATION_TIMESTAMP_KEY);
        sessionStorage.removeItem(NAVIGATION_URL_KEY);
      } catch (_e) {
        // Session storage might not be available
      }
    }
    // For mobile platforms, navigation state is handled by React Navigation
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    this.clearNavigationState();
    if (Platform.OS === 'web') {
      window.location.href = '/';
    } else {
      // On mobile, reset by clearing error state
      // The app will re-render from root
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
      });
    }
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const safeError = safeExtractError(this.state.error);
      const isDev = dev.enableDebugging();

      return (
        <View style={styles.main}>
          <ScrollView contentContainerStyle={styles.scrollContent} style={styles.scrollView}>
            <Text style={styles.exclamation}>Something went wrong</Text>

            {isDev ? (
              // Development mode: Show full error details
              <>
                <Text style={styles.errorName}>{safeError.name}</Text>
                <Text style={styles.errorMessage}>{safeError.message}</Text>

                {this.props.message ? (
                  <Text style={styles.contextMessage}>{this.props.message}</Text>
                ) : null}

                {safeError.stack ? (
                  <>
                    <Text style={styles.stackLabel}>Stack Trace:</Text>
                    <MonospaceText style={styles.stackTrace}>{safeError.stack}</MonospaceText>
                  </>
                ) : null}

                {this.state.errorInfo?.componentStack ? (
                  <>
                    <Text style={styles.stackLabel}>Component Stack:</Text>
                    <MonospaceText style={styles.stackTrace}>
                      {this.state.errorInfo.componentStack}
                    </MonospaceText>
                  </>
                ) : null}
              </>
            ) : (
              // Production mode: User-friendly message
              <>
                <Text style={styles.productionMessage}>
                  We&apos;re sorry, but something unexpected happened.
                </Text>
                <Text style={styles.productionSubtext}>
                  Our team has been notified and is looking into it.
                </Text>
                {safeError.message ? (
                  <Text style={styles.errorHint}>Error: {safeError.message}</Text>
                ) : null}
              </>
            )}

            {/* Recovery Buttons */}
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.button, styles.retryButton]}
                onPress={this.handleRetry}
                testID="error-boundary-retry">
                <Text style={styles.buttonText}>Try Again</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.button, styles.homeButton]}
                onPress={this.handleGoHome}
                testID="error-boundary-go-home">
                <Text style={styles.buttonText}>Go Home</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

const styles = StyleSheet.create({
  main: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  exclamation: {
    fontSize: 28,
    color: theme.colour.text.heading.primary,
    fontWeight: '700',
    marginBottom: 16,
    textAlign: 'center',
  },
  errorName: {
    fontSize: 18,
    color: '#D32F2F',
    fontWeight: '600',
    marginTop: 12,
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: 16,
    color: theme.colour.text.heading.primary,
    fontWeight: '500',
    marginTop: 8,
    marginBottom: 12,
    textAlign: 'center',
    paddingHorizontal: 16,
  },
  contextMessage: {
    fontSize: 14,
    color: theme.colour.text.content.primary,
    marginTop: 8,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  productionMessage: {
    fontSize: 16,
    color: theme.colour.text.heading.primary,
    fontWeight: '500',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
    paddingHorizontal: 24,
  },
  productionSubtext: {
    fontSize: 14,
    color: theme.colour.text.content.primary,
    marginBottom: 12,
    textAlign: 'center',
    paddingHorizontal: 24,
  },
  errorHint: {
    fontSize: 13,
    color: '#666666',
    marginTop: 8,
    fontStyle: 'italic',
    textAlign: 'center',
    paddingHorizontal: 16,
  },
  stackLabel: {
    fontSize: 14,
    color: theme.colour.text.heading.primary,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
    alignSelf: 'flex-start',
  },
  stackTrace: {
    fontSize: 12,
    color: theme.colour.text.content.primary,
    backgroundColor: '#F5F5F5',
    padding: 12,
    borderRadius: 4,
    marginBottom: 12,
    alignSelf: 'stretch',
  },
  buttonContainer: {
    flexDirection: 'row',
    marginTop: 24,
    paddingHorizontal: 16,
  },
  button: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 120,
    marginHorizontal: 6,
  },
  retryButton: {
    backgroundColor: theme.colour.general.primary,
  },
  homeButton: {
    backgroundColor: '#666666',
  },
  buttonText: {
    fontSize: 16,
    color: '#FFFFFF',
    fontWeight: '600',
  },
});
